import { useEffect, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  FiMail,
  FiSearch,
  FiX,
  FiPlus,
  FiSend,
  FiTrash2,
  FiEdit2,
  FiCalendar,
  FiUsers,
  FiClock,
  FiAlertCircle,
  FiPlay,
  FiZap,
  FiHelpCircle,
  FiChevronDown,
  FiSlash,
  FiRefreshCw,
  FiCopy,
} from "react-icons/fi";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { CardSkeleton } from "@/components/common";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";

/**
 * Plain-English explainer card aimed at the broker, not engineers.
 * Avoids "queue", "worker", "DLQ", etc. — uses postal metaphors.
 */
function HowSendingWorksCard() {
  const [open, setOpen] = useState(false);
  return (
    <div className="rounded-lg border border-slate-200 bg-white shadow-sm">
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center justify-between gap-3 px-4 py-3 hover:bg-slate-50 transition-colors text-left"
      >
        <span className="flex items-center gap-2.5">
          <FiHelpCircle className="w-4 h-4 text-slate-500" />
          <span className="text-sm font-medium text-slate-900">How sending works</span>
        </span>
        <FiChevronDown className={`w-4 h-4 text-slate-400 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="px-4 pb-4 pt-0 border-t border-slate-100 space-y-4 text-sm text-slate-700 leading-relaxed">
          <div>
            <p className="font-semibold text-slate-900 mb-1">After you hit Send or set a schedule</p>
            <p>
              Your campaign is saved right away. The emails themselves go out shortly after,
              once they reach the front of the sending line. Most campaigns start delivering
              within a couple of minutes.
            </p>
          </div>
          <div>
            <p className="font-semibold text-slate-900 mb-1">Why there's a short wait</p>
            <ul className="list-disc list-inside space-y-1">
              <li>It keeps you safely under the email provider's rate limit, so messages aren't blocked or marked as spam.</li>
              <li>If the provider has a momentary issue, the system simply waits a bit and tries again rather than giving up.</li>
              <li>You can cancel a campaign any time before it finishes, and anyone who hasn't been emailed yet will be skipped.</li>
            </ul>
          </div>
          <div>
            <p className="font-semibold text-slate-900 mb-1">How fast it sends</p>
            <p>
              Around 600 emails a minute once delivery is underway. A 2,000-recipient list typically
              finishes in 3 to 5 minutes. Larger lists just take a little longer.
            </p>
          </div>
          <div>
            <p className="font-semibold text-slate-900 mb-1">If an email can't be delivered</p>
            <p>
              The system retries up to 3 times, waiting longer each time (30 seconds, 1 minute,
              then 2 minutes). If it still can't get through (usually a bad address or full inbox),
              that one is marked as failed so you can see it later. Other recipients aren't affected.
            </p>
          </div>
          <div>
            <p className="font-semibold text-slate-900 mb-1">How to keep an eye on things</p>
            <ul className="list-disc list-inside space-y-1">
              <li>The Last run panel above shows the most recent check, with a green tick when everything's fine.</li>
              <li>Each campaign card updates its sent count as emails go out.</li>
              <li>Click Recipients on a sent campaign to see exactly who got it and when.</li>
              <li>Want to send straight away instead of waiting up to 5 minutes? Click <strong>Run now</strong>.</li>
              <li>For full technical history, open <Link to="/admin/cron-logs" className="underline">cron logs</Link>.</li>
            </ul>
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Monthly email-quota tracker.
 *
 * Pulls /api/admin/email-quota every 60s and renders a small card showing
 * "X of Y emails this month" with a progress bar + countdown to the month
 * reset (start of the next UTC month). Helps the broker pace big campaigns
 * under the Resend plan's monthly allowance.
 *
 * Colours:
 *   < 60% used → emerald (healthy)
 *   60-90%     → amber  (caution, slow new big campaigns)
 *   > 90%      → red    (very close, defer new sends)
 */
interface EmailQuota {
  sentThisMonth: number;
  monthlyLimit: number;
  remainingThisMonth: number;
  nextResetUtc: string;
  campaignSendsThisMonth: number;
  automationSendsThisMonth: number;
  transactionalSendsThisMonth: number;
  transactionalByType: Record<string, number>;
}

function EmailQuotaCard() {
  const [quota, setQuota] = useState<EmailQuota | null>(null);
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    let cancelled = false;
    const fetchQuota = async () => {
      try {
        const res = await fetch("/api/admin/email-quota", { credentials: "include" });
        if (!res.ok) return;
        const data = (await res.json()) as EmailQuota;
        if (!cancelled) setQuota(data);
      } catch {
        // silent — the card just stays in loading state
      }
    };
    fetchQuota();
    const id = setInterval(fetchQuota, 60_000);
    // Countdown only shows days/hours/minutes, so a 1-minute tick is plenty.
    const tick = setInterval(() => setNow(Date.now()), 60_000);
    return () => {
      cancelled = true;
      clearInterval(id);
      clearInterval(tick);
    };
  }, []);

  if (!quota) {
    return (
      <div className="rounded-lg border border-slate-200 bg-white shadow-sm px-4 py-3 text-sm text-slate-500">
        Loading email quota…
      </div>
    );
  }

  const pct = Math.min(100, Math.round((quota.sentThisMonth / quota.monthlyLimit) * 100));
  const tone =
    pct < 60 ? { bar: "bg-emerald-500", text: "text-emerald-700", chipBg: "bg-emerald-50 border-emerald-200" }
    : pct < 90 ? { bar: "bg-amber-500", text: "text-amber-700", chipBg: "bg-amber-50 border-amber-200" }
    : { bar: "bg-red-500", text: "text-red-700", chipBg: "bg-red-50 border-red-200" };

  // Countdown to the start of next month (UTC)
  const resetAtMs = new Date(quota.nextResetUtc).getTime();
  const msToReset = Math.max(0, resetAtMs - now);
  const daysToReset = Math.floor(msToReset / 86_400_000);
  const hoursToReset = Math.floor((msToReset % 86_400_000) / 3_600_000);
  const minsToReset = Math.floor((msToReset % 3_600_000) / 60_000);
  const resetLabel = daysToReset > 0
    ? `${daysToReset}d ${hoursToReset}h`
    : hoursToReset > 0
      ? `${hoursToReset}h ${String(minsToReset).padStart(2, "0")}m`
      : `${minsToReset}m`;

  return (
    <div className="rounded-lg border border-slate-200 bg-white shadow-sm overflow-hidden">
      <div className="px-4 py-3">
        <div className="flex items-center justify-between gap-3 mb-2">
          <div className="flex items-center gap-2">
            <FiMail className="w-4 h-4 text-slate-500" />
            <span className="text-sm font-medium text-slate-900">Email usage this month</span>
          </div>
          <div className="flex items-center gap-2 text-xs">
            <span className={`px-2 py-0.5 rounded-full border tabular-nums font-medium ${tone.chipBg} ${tone.text}`}>
              {quota.sentThisMonth.toLocaleString()} / {quota.monthlyLimit.toLocaleString()}
            </span>
            <span className="text-slate-500 hidden sm:inline">·</span>
            <span className="text-slate-500 tabular-nums hidden sm:inline">
              Resets in {resetLabel}
            </span>
          </div>
        </div>

        {/* Progress bar */}
        <div className="h-2 rounded-full bg-slate-100 overflow-hidden">
          <div
            className={`h-full rounded-full ${tone.bar} transition-all duration-500`}
            style={{ width: `${pct}%` }}
          />
        </div>

        <div className="mt-2 flex items-center justify-between gap-3 text-[11px] text-slate-500 flex-wrap">
          <div className="flex items-center gap-3 flex-wrap">
            <span>
              <span className="tabular-nums font-medium text-slate-700">{quota.campaignSendsThisMonth.toLocaleString()}</span> campaign
            </span>
            <span>
              <span className="tabular-nums font-medium text-slate-700">{quota.automationSendsThisMonth.toLocaleString()}</span> automation
            </span>
            <span title={Object.entries(quota.transactionalByType).map(([k, v]) => `${k}: ${v}`).join("\n") || "OTP login, contact form, rate-change alerts, etc."}>
              <span className="tabular-nums font-medium text-slate-700">{quota.transactionalSendsThisMonth.toLocaleString()}</span> transactional
            </span>
            <span>
              <span className="tabular-nums font-medium text-slate-700">{quota.remainingThisMonth.toLocaleString()}</span> remaining this month
            </span>
          </div>
          <span className="sm:hidden tabular-nums">Resets in {resetLabel}</span>
        </div>
      </div>

      {pct >= 90 && (
        <div className="px-4 py-2 border-t border-red-200 bg-red-50 text-xs text-red-800 flex items-start gap-2">
          <FiAlertCircle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
          <span>
            Close to the monthly cap. Defer non-urgent sends until the month resets — going over the
            Resend plan limit may block transactional mail (login OTPs, contact form, etc.).
          </span>
        </div>
      )}
    </div>
  );
}

function computeNextCron(): { date: Date; ms: number } {
  // Cron runs every 5 minutes (UTC). Round the current time UP to the next
  // multiple-of-5 minute. If we're already exactly on a 5-minute boundary,
  // the next run is 5 minutes from now.
  const now = new Date();
  const next = new Date(now);
  next.setUTCSeconds(0, 0);
  const currentMin = next.getUTCMinutes();
  const nextSlot = Math.floor(currentMin / 5) * 5 + 5;
  next.setUTCMinutes(nextSlot);
  if (next.getTime() <= now.getTime()) {
    next.setUTCMinutes(next.getUTCMinutes() + 5);
  }
  return { date: next, ms: next.getTime() - now.getTime() };
}

function formatRelativeTime(ms: number): string {
  const abs = Math.abs(ms);
  const min = Math.floor(abs / 60000);
  if (min < 1) return "just now";
  if (min < 60) return `${min}m ago`;
  const hours = Math.floor(min / 60);
  if (hours < 24) return `${hours}h ${min % 60}m ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

/**
 * Parse the cron resultSummary string into compact key/value chips.
 * Backend writes:
 *   "Emails: 3 sent, 1 failed of 4 due. Campaigns: 2 sent, 0 failed"
 * which we surface as small "Emails 3/4" / "Campaigns 2 sent" pills so the
 * banner stays readable at narrow widths.
 */
function parseSummaryChips(summary: string | null): { label: string; value: string; tone: "ok" | "warn" | "muted" }[] {
  if (!summary) return [];
  const chips: { label: string; value: string; tone: "ok" | "warn" | "muted" }[] = [];
  const emailsMatch = summary.match(/Emails:\s*(\d+)\s*sent,\s*(\d+)\s*failed\s*of\s*(\d+)\s*due/i);
  if (emailsMatch) {
    const [, sent, failed, due] = emailsMatch;
    if (Number(due) > 0 || Number(sent) > 0) {
      chips.push({
        label: "Emails",
        value: `${sent}/${due}`,
        tone: Number(failed) > 0 ? "warn" : "ok",
      });
    }
  }
  const campaignsMatch = summary.match(/Campaigns:\s*(\d+)\s*sent,\s*(\d+)\s*failed/i);
  if (campaignsMatch) {
    const [, sent, failed] = campaignsMatch;
    if (Number(sent) > 0 || Number(failed) > 0) {
      chips.push({
        label: "Campaigns",
        value: Number(failed) > 0 ? `${sent} sent, ${failed} failed` : `${sent} sent`,
        tone: Number(failed) > 0 ? "warn" : "ok",
      });
    }
  }
  if (chips.length === 0) {
    chips.push({ label: "", value: "no work due", tone: "muted" });
  }
  return chips;
}

function CronLastRunSummary({ log }: { log: CronLogEntry }) {
  const ago = formatRelativeTime(Date.now() - new Date(log.startedAt).getTime());
  const statusIcon = log.status === "success" ? "✓" : log.status === "error" ? "✗" : "•";
  const statusColor =
    log.status === "success" ? "text-emerald-700" :
    log.status === "error" ? "text-red-700" :
    "text-slate-700";

  if (log.status === "error") {
    return (
      <div className="min-w-0">
        <p className="text-sm font-medium text-slate-900">
          <span className={statusColor}>{statusIcon}</span>{" "}
          <span className="tabular-nums">{ago}</span>
        </p>
        <p className="text-xs text-red-700 mt-0.5 break-words line-clamp-2">
          {log.errorMessage || "Error"}
        </p>
      </div>
    );
  }

  const chips = parseSummaryChips(log.resultSummary);
  return (
    <div className="min-w-0">
      <p className="text-sm font-medium text-slate-900">
        <span className={statusColor}>{statusIcon}</span>{" "}
        <span className="tabular-nums">{ago}</span>
      </p>
      <div className="flex flex-wrap gap-1 mt-1">
        {chips.map((c, i) => (
          <span
            key={i}
            className={`text-[11px] px-1.5 py-0.5 rounded border tabular-nums ${
              c.tone === "ok" ? "bg-emerald-50 text-emerald-700 border-emerald-200" :
              c.tone === "warn" ? "bg-amber-50 text-amber-700 border-amber-200" :
              "bg-slate-50 text-slate-600 border-slate-200"
            }`}
          >
            {c.label && <span className="font-medium">{c.label} </span>}
            {c.value}
          </span>
        ))}
      </div>
    </div>
  );
}

type CronLogEntry = {
  id: string;
  jobName: string;
  trigger: string;
  status: "running" | "success" | "error";
  startedAt: string;
  completedAt: string | null;
  durationMs: number | null;
  resultSummary: string | null;
  errorMessage: string | null;
};

function formatDuration(ms: number): string {
  if (ms <= 0) return "now";
  // Under one minute, surface seconds so the countdown actually moves in the
  // final stretch instead of sitting on "0m" or "1m" until the cron fires.
  if (ms < 60_000) {
    const sec = Math.max(1, Math.ceil(ms / 1000));
    return `${sec}s`;
  }
  const totalMin = Math.floor(ms / 60000);
  const days = Math.floor(totalMin / (60 * 24));
  const hours = Math.floor((totalMin % (60 * 24)) / 60);
  const mins = totalMin % 60;
  const parts: string[] = [];
  if (days) parts.push(`${days}d`);
  if (hours) parts.push(`${hours}h`);
  if (mins || (!days && !hours)) parts.push(`${mins}m`);
  return parts.join(" ");
}

type Campaign = {
  id: string;
  name: string;
  subject: string;
  previewText: string | null;
  content: string;
  status: "draft" | "scheduled" | "sending" | "sent" | "cancelled";
  segment: "all" | "newsletter" | "rate_alerts" | "market_updates" | "promotions";
  scheduledFor: string | null;
  totalRecipients: number;
  totalSent: number;
  totalOpened: number;
  totalClicked: number;
  sentAt: string | null;
  createdAt: string;
};

const statusConfig: Record<string, { label: string; color: string; icon: React.ComponentType<{ className?: string }> }> = {
  draft: { label: "Draft", color: "bg-gray-100 text-gray-700", icon: FiEdit2 },
  scheduled: { label: "Scheduled", color: "bg-blue-100 text-blue-700", icon: FiClock },
  sending: { label: "Sending", color: "bg-yellow-100 text-yellow-700", icon: FiSend },
  sent: { label: "Sent", color: "bg-green-100 text-green-700", icon: FiMail },
  cancelled: { label: "Cancelled", color: "bg-red-100 text-red-700", icon: FiX },
};

const segmentLabels: Record<string, string> = {
  all: "All Subscribers",
  newsletter: "Newsletter",
  rate_alerts: "Rate Alerts",
  market_updates: "Market Updates",
  promotions: "Promotions",
};

export function CampaignsPage() {
  const navigate = useNavigate();
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [sendModalOpen, setSendModalOpen] = useState(false);
  const [selectedCampaign, setSelectedCampaign] = useState<Campaign | null>(null);
  const [isSending, setIsSending] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [processingCampaignId, setProcessingCampaignId] = useState<string | null>(null);
  const [processProgress, setProcessProgress] = useState<{ sent: number; remaining: number; total: number } | null>(null);
  const fetchCampaigns = useCallback(async () => {
    try {
      const response = await fetch("/api/admin/campaigns", {
        credentials: "include",
      });
      if (response.ok) {
        const data = (await response.json()) as Campaign[];
        setCampaigns(data);
      }
    } catch (error) {
      console.error("Failed to fetch campaigns:", error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCampaigns();
  }, [fetchCampaigns]);

  // Auto-refresh the campaign list so the broker doesn't have to hit
  // F5 to see status changes. The cadence adapts to what's happening:
  //
  //   - 2s when something is actively in motion: a campaign is "sending"
  //     (the consumer is draining sends), or one is "scheduled" with a past
  //     scheduledFor (about to be picked up by the next cron tick).
  //   - 8s otherwise (just to catch out-of-band changes, e.g. someone else
  //     edits a campaign in another tab).
  //
  // Pause polling while the document is hidden so we don't burn cycles on
  // background tabs.
  const hasActiveCampaigns = campaigns.some(c => {
    if (c.status === "sending") return true;
    if (c.status === "scheduled" && c.scheduledFor) {
      return new Date(c.scheduledFor).getTime() <= Date.now();
    }
    return false;
  });
  useEffect(() => {
    const intervalMs = hasActiveCampaigns ? 2000 : 8000;
    const tick = () => {
      if (typeof document !== "undefined" && document.visibilityState === "hidden") return;
      fetchCampaigns();
    };
    const interval = setInterval(tick, intervalMs);
    // Also refresh once when the tab becomes visible again, so the user
    // sees the latest state immediately on tab switch instead of waiting
    // up to 8 more seconds.
    const onVisible = () => {
      if (document.visibilityState === "visible") fetchCampaigns();
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      clearInterval(interval);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [hasActiveCampaigns, fetchCampaigns]);

  // Filter campaigns
  const filteredCampaigns = campaigns.filter((campaign) => {
    const matchesSearch =
      campaign.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      campaign.subject.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus = statusFilter === "all" || campaign.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const handleSend = async () => {
    if (!selectedCampaign) return;

    setIsSending(true);
    try {
      const response = await fetch(`/api/admin/campaigns/${selectedCampaign.id}/send`, {
        method: "POST",
        credentials: "include",
      });

      const data = (await response.json()) as { message?: string; error?: string; data?: { recipientCount: number } };

      if (response.ok) {
        toast.success(data.message || "Campaign sent successfully");
        fetchCampaigns();
        setSendModalOpen(false);
      } else {
        toast.error(data.error || "Failed to send campaign");
      }
    } catch (error) {
      toast.error("Failed to send campaign");
    } finally {
      setIsSending(false);
      setSelectedCampaign(null);
    }
  };

  // Re-send / restart confirmation flow
  const [restartModalOpen, setRestartModalOpen] = useState(false);
  const [isRestarting, setIsRestarting] = useState(false);
  // datetime-local value. Empty = "send now". A future value = re-schedule.
  const [restartScheduledFor, setRestartScheduledFor] = useState("");
  const openRestartDialog = (campaign: Campaign) => {
    setSelectedCampaign(campaign);
    setRestartScheduledFor("");
    setRestartModalOpen(true);
  };
  const handleRestartConfirm = async () => {
    if (!selectedCampaign) return;
    setIsRestarting(true);
    try {
      const body: { scheduledFor?: string } = {};
      if (restartScheduledFor) {
        // datetime-local is a naive wall-clock string. Convert to a UTC
        // ISO instant the same way the editor's save path does so the
        // worker (UTC runtime) doesn't misinterpret it.
        body.scheduledFor = new Date(restartScheduledFor).toISOString();
      }
      const response = await fetch(`/api/admin/campaigns/${selectedCampaign.id}/restart`, {
        method: "POST",
        credentials: "include",
        headers: { "content-type": "application/json" },
        body: JSON.stringify(body),
      });
      const data = (await response.json()) as { error?: string; message?: string };
      if (!response.ok) {
        toast.error(data.error || "Failed to restart campaign");
      } else {
        toast.success(data.message || "Campaign queued to re-send");
        fetchCampaigns();
        setRestartModalOpen(false);
      }
    } catch {
      toast.error("Failed to restart campaign");
    } finally {
      setIsRestarting(false);
      setSelectedCampaign(null);
    }
  };

  // Duplicate: clones a campaign as a fresh draft and navigates straight to
  // the editor for the new copy so the broker can tweak before scheduling.
  const [duplicatingCampaignId, setDuplicatingCampaignId] = useState<string | null>(null);
  const handleDuplicate = async (campaign: Campaign) => {
    setDuplicatingCampaignId(campaign.id);
    try {
      const response = await fetch(`/api/admin/campaigns/${campaign.id}/duplicate`, {
        method: "POST",
        credentials: "include",
      });
      const data = (await response.json()) as {
        data?: { id: string };
        error?: string;
        message?: string;
      };
      if (!response.ok || !data.data?.id) {
        toast.error(data.error || "Failed to duplicate campaign");
        return;
      }
      toast.success(data.message || "Duplicated");
      navigate(`/admin/campaigns/edit/${data.data.id}`);
    } catch {
      toast.error("Failed to duplicate campaign");
    } finally {
      setDuplicatingCampaignId(null);
    }
  };

  // Recipient drill-down: list every campaign_send row for a campaign so the
  // broker can see exactly who got the email and when.
  type SendRow = {
    id: string;
    email: string;
    status: "pending" | "sent" | "delivered" | "bounced" | "failed";
    sentAt: string | null;
    openedAt: string | null;
    clickedAt: string | null;
  };
  const [recipientsModalOpen, setRecipientsModalOpen] = useState(false);
  const [recipientRows, setRecipientRows] = useState<SendRow[] | null>(null);
  const [recipientSearch, setRecipientSearch] = useState("");
  const openRecipients = async (campaign: Campaign) => {
    setSelectedCampaign(campaign);
    setRecipientRows(null);
    setRecipientSearch("");
    setRecipientsModalOpen(true);
    try {
      const response = await fetch(`/api/admin/campaigns/${campaign.id}/sends`, {
        credentials: "include",
      });
      if (!response.ok) {
        toast.error("Failed to load recipients");
        setRecipientRows([]);
        return;
      }
      const data = (await response.json()) as SendRow[];
      setRecipientRows(data);
    } catch {
      toast.error("Failed to load recipients");
      setRecipientRows([]);
    }
  };

  const [cancelModalOpen, setCancelModalOpen] = useState(false);
  const [isCancelling, setIsCancelling] = useState(false);
  const handleCancelConfirm = async () => {
    if (!selectedCampaign) return;
    setIsCancelling(true);
    try {
      const response = await fetch(`/api/admin/campaigns/${selectedCampaign.id}/cancel`, {
        method: "POST",
        credentials: "include",
      });
      const data = (await response.json()) as { error?: string; message?: string };
      if (!response.ok) {
        toast.error(data.error || "Failed to cancel campaign");
      } else {
        toast.success(data.message || "Campaign cancelled");
        fetchCampaigns();
        setCancelModalOpen(false);
      }
    } catch {
      toast.error("Failed to cancel campaign");
    } finally {
      setIsCancelling(false);
      setSelectedCampaign(null);
    }
  };

  const handleDelete = async () => {
    if (!selectedCampaign) return;

    setIsDeleting(true);
    try {
      const response = await fetch(`/api/admin/campaigns/${selectedCampaign.id}`, {
        method: "DELETE",
        credentials: "include",
      });

      if (response.ok) {
        toast.success("Campaign deleted");
        fetchCampaigns();
        setDeleteModalOpen(false);
      } else {
        const data = (await response.json()) as { error?: string };
        toast.error(data.error || "Failed to delete campaign");
      }
    } catch (error) {
      toast.error("Failed to delete campaign");
    } finally {
      setIsDeleting(false);
      setSelectedCampaign(null);
    }
  };

  const [isRunningCron, setIsRunningCron] = useState(false);

  // Cron fires every 5 minutes (UTC) — see workers/cron-jobs/wrangler.toml.
  // Tick once a second so the countdown's final-minute seconds display moves
  // smoothly. The cost is one tiny in-memory recalc, no network traffic.
  const [nextCron, setNextCron] = useState(() => computeNextCron());
  useEffect(() => {
    const id = setInterval(() => setNextCron(computeNextCron()), 1_000);
    return () => clearInterval(id);
  }, []);

  const [lastCronLog, setLastCronLog] = useState<CronLogEntry | null>(null);
  const fetchLastCronLog = useCallback(async () => {
    try {
      const res = await fetch("/api/admin/cron-logs", { credentials: "include" });
      if (!res.ok) return;
      const logs = (await res.json()) as CronLogEntry[];
      const lastSendRun = logs.find(
        l => l.jobName === "send_scheduled_emails" && l.status !== "running"
      );
      setLastCronLog(lastSendRun ?? null);
    } catch {
      // silent — banner just falls back to "next run" only
    }
  }, []);
  useEffect(() => {
    fetchLastCronLog();
    // 15s — fast enough to show a fresh "Last run" within a tick or two
    // of the cron firing, slow enough to be a tiny D1 cost.
    const id = setInterval(fetchLastCronLog, 15_000);
    return () => clearInterval(id);
  }, [fetchLastCronLog]);

  const handleRunCronNow = async () => {
    setIsRunningCron(true);
    try {
      const response = await fetch("/api/cron/send-scheduled-emails", {
        method: "POST",
        credentials: "include",
      });
      const data = (await response.json()) as {
        data?: {
          emails?: { sent: number; failed: number; total: number };
          campaigns?: { sent: number; failed: number; total: number };
        };
        error?: string;
      };
      if (!response.ok) {
        toast.error(data.error || "Cron run failed");
      } else {
        const e = data.data?.emails;
        const c = data.data?.campaigns;
        const parts: string[] = [];
        if (c) parts.push(`Campaigns: ${c.sent}/${c.total} due processed${c.failed ? `, ${c.failed} failed` : ""}`);
        if (e) parts.push(`Automations: ${e.sent}/${e.total} sent${e.failed ? `, ${e.failed} failed` : ""}`);
        toast.success(parts.length ? `Cron ran — ${parts.join(" | ")}` : "Cron ran successfully");
        fetchCampaigns();
        fetchLastCronLog();
      }
    } catch {
      toast.error("Cron run failed");
    } finally {
      setIsRunningCron(false);
    }
  };

  const handleProcessQueue = async (campaignId: string) => {
    setProcessingCampaignId(campaignId);
    setProcessProgress(null);

    try {
      // Keep calling process-queue until all sends are done
      let remaining = 1;
      while (remaining > 0) {
        const response = await fetch(`/api/admin/campaigns/${campaignId}/process-queue`, {
          method: "POST",
          credentials: "include",
        });

        const data = (await response.json()) as {
          data?: { sent: number; remaining: number; total: number };
          error?: string;
        };

        if (!response.ok) {
          toast.error(data.error || "Failed to process queue");
          break;
        }

        if (data.data) {
          remaining = data.data.remaining;
          setProcessProgress({
            sent: data.data.total - data.data.remaining,
            remaining: data.data.remaining,
            total: data.data.total,
          });
        } else {
          break;
        }
      }

      toast.success("Campaign emails sent!");
      fetchCampaigns();
    } catch {
      toast.error("Failed to process queue");
    } finally {
      setProcessingCampaignId(null);
      setProcessProgress(null);
    }
  };

  const stats = {
    total: campaigns.length,
    draft: campaigns.filter((c) => c.status === "draft").length,
    sent: campaigns.filter((c) => c.status === "sent").length,
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-serif text-3xl font-medium text-slate-900">
            Email Campaigns
          </h1>
          <p className="text-muted-foreground mt-1">
            Create and manage email campaigns to your subscribers
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            onClick={handleRunCronNow}
            disabled={isRunningCron}
            title="Manually run the scheduled-email cron now (processes due automations + scheduled campaigns)"
          >
            <FiZap className={`w-4 h-4 mr-2 ${isRunningCron ? "animate-pulse" : ""}`} />
            {isRunningCron ? "Running..." : "Run send queue now"}
          </Button>
          <Button asChild>
            <Link to="/admin/campaigns/new">
              <FiPlus className="w-4 h-4 mr-2" />
              New Campaign
            </Link>
          </Button>
        </div>
      </div>

      {/* Cron status banner — next run + last run from cron_logs */}
      <div className="rounded-lg border border-slate-200 bg-white shadow-sm">
        <div className="flex items-start justify-between gap-3 px-4 py-3">
          <div className="flex items-start gap-3 min-w-0 flex-1">
            <div className="p-2 rounded-md bg-slate-100 shrink-0">
              <FiClock className="w-4 h-4 text-slate-700" />
            </div>
            <div className="min-w-0 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3 flex-1">
              <div className="min-w-0">
                <p className="text-[11px] font-medium uppercase tracking-wide text-slate-500">Next run</p>
                <p className="text-sm font-medium text-slate-900">
                  in <span className="tabular-nums font-semibold">{formatDuration(nextCron.ms)}</span>
                  <span className="text-slate-500 font-normal">
                    {" · "}
                    {nextCron.date.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" })}
                  </span>
                </p>
              </div>
              <div className="min-w-0">
                <p className="text-[11px] font-medium uppercase tracking-wide text-slate-500">Last run</p>
                {lastCronLog ? (
                  <CronLastRunSummary log={lastCronLog} />
                ) : (
                  <p className="text-sm text-slate-500">No runs yet</p>
                )}
              </div>
            </div>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={handleRunCronNow}
            disabled={isRunningCron}
            className="shrink-0"
          >
            <FiZap className={`w-3.5 h-3.5 mr-1.5 ${isRunningCron ? "animate-pulse" : ""}`} />
            {isRunningCron ? "Running..." : "Run now"}
          </Button>
        </div>
      </div>
      <p className="text-[11px] text-slate-500 -mt-3 pl-1">
        Cron fires every 5 minutes (UTC). Sends are queued and delivered in the background — see "How sending works" below for details. Last 100 runs at <Link to="/admin/cron-logs" className="underline hover:text-slate-700">cron logs</Link>.
      </p>

      {/* Plain-English explainer for non-technical users */}
      <HowSendingWorksCard />

      {/* Daily email quota tracker — keeps usage under Resend caps + reserves
          headroom for operational mail (OTP login, contact-form notifications)
          that bypass this tracker. */}
      <EmailQuotaCard />

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
        <Card>
          <CardContent className="pt-4 pb-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-primary/10 rounded-lg">
                <FiMail className="w-5 h-5 text-primary" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats.total}</p>
                <p className="text-xs text-muted-foreground">Total</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4 pb-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-gray-100 rounded-lg">
                <FiEdit2 className="w-5 h-5 text-gray-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats.draft}</p>
                <p className="text-xs text-muted-foreground">Drafts</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4 pb-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <FiSend className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats.sent}</p>
                <p className="text-xs text-muted-foreground">Sent</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground w-4 h-4" />
          <Input
            placeholder="Search campaigns..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
          />
          {searchQuery && (
            <button
              onClick={() => setSearchQuery("")}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
            >
              <FiX className="w-4 h-4" />
            </button>
          )}
        </div>
        <div className="flex gap-2 flex-wrap">
          {["all", "draft", "scheduled", "sending", "sent", "cancelled"].map((status) => (
            <Button
              key={status}
              variant={statusFilter === status ? "default" : "outline"}
              size="sm"
              onClick={() => setStatusFilter(status)}
              className="capitalize"
            >
              {status}
            </Button>
          ))}
        </div>
      </div>

      {/* Campaigns List */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FiMail className="w-5 h-5" />
            All Campaigns ({filteredCampaigns.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="space-y-3">
              <CardSkeleton />
              <CardSkeleton />
              <CardSkeleton />
            </div>
          ) : filteredCampaigns.length === 0 ? (
            <div className="text-center py-12">
              <FiMail className="w-12 h-12 mx-auto text-muted-foreground/50 mb-4" />
              <p className="text-muted-foreground mb-4">
                {searchQuery || statusFilter !== "all"
                  ? "No campaigns found"
                  : "No campaigns yet"}
              </p>
              {!searchQuery && statusFilter === "all" && (
                <Button asChild>
                  <Link to="/admin/campaigns/new">
                    <FiPlus className="w-4 h-4 mr-2" />
                    Create Your First Campaign
                  </Link>
                </Button>
              )}
            </div>
          ) : (
            <div className="space-y-4">
              {filteredCampaigns.map((campaign) => {
                const StatusIcon = statusConfig[campaign.status]?.icon || FiMail;

                return (
                  <div
                    key={campaign.id}
                    className="flex flex-col lg:flex-row lg:items-center gap-4 p-4 rounded-lg bg-muted/50 hover:bg-muted/70 transition-colors"
                  >
                    {/* Campaign Info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start gap-3">
                        <div className={`p-2 rounded-lg shrink-0 ${statusConfig[campaign.status]?.color || "bg-gray-100"}`}>
                          <StatusIcon className="w-4 h-4" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2 flex-wrap">
                            <h3 className="font-medium truncate">{campaign.name}</h3>
                            <Badge
                              variant="secondary"
                              className={statusConfig[campaign.status]?.color || ""}
                            >
                              {statusConfig[campaign.status]?.label || campaign.status}
                            </Badge>
                          </div>
                          <p className="text-sm text-muted-foreground truncate mt-0.5">
                            {campaign.subject}
                          </p>
                          <div className="flex items-center gap-4 mt-2 text-xs text-muted-foreground flex-wrap">
                            <span className="flex items-center gap-1">
                              <FiUsers className="w-3 h-3" />
                              {segmentLabels[campaign.segment] || campaign.segment}
                            </span>
                            {/* Show the scheduled wall-clock time when the campaign
                                has one (scheduled / sending / cancelled-after-schedule).
                                Falls back to the created-at date for drafts and old sent
                                campaigns so the card never goes blank. */}
                            {campaign.scheduledFor ? (
                              <span
                                className="flex items-center gap-1"
                                title={`Scheduled ${new Date(campaign.scheduledFor).toLocaleString()}`}
                              >
                                <FiClock className="w-3 h-3" />
                                {campaign.status === "scheduled" || campaign.status === "sending"
                                  ? "Sends "
                                  : campaign.status === "sent"
                                    ? "Was due "
                                    : "Was scheduled "}
                                {new Date(campaign.scheduledFor).toLocaleString(undefined, {
                                  month: "short",
                                  day: "numeric",
                                  hour: "numeric",
                                  minute: "2-digit",
                                })}
                              </span>
                            ) : (
                              <span className="flex items-center gap-1">
                                <FiCalendar className="w-3 h-3" />
                                Created {new Date(campaign.createdAt).toLocaleDateString()}
                              </span>
                            )}
                            {campaign.sentAt && (
                              <span className="flex items-center gap-1">
                                <FiSend className="w-3 h-3" />
                                Sent {new Date(campaign.sentAt).toLocaleDateString()}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Stats */}
                    {campaign.status === "sent" && campaign.totalSent > 0 && (
                      <div className="flex items-center gap-4 px-4 py-2 bg-background rounded-lg border">
                        <div className="text-center">
                          <p className="text-lg font-bold tabular-nums">{campaign.totalSent}</p>
                          <p className="text-xs text-muted-foreground">Sent</p>
                        </div>
                      </div>
                    )}

                    {/* Sending progress + Send Now button */}
                    {campaign.status === "sending" && (
                      <div className="flex items-center gap-3 shrink-0">
                        {processingCampaignId === campaign.id && processProgress ? (
                          <div className="space-y-1.5 min-w-[180px]">
                            <div className="flex justify-between text-xs text-muted-foreground">
                              <span>{processProgress.sent} / {processProgress.total}</span>
                              <span>{Math.round((processProgress.sent / processProgress.total) * 100)}%</span>
                            </div>
                            <div className="w-full bg-slate-200 rounded-full h-2 overflow-hidden">
                              <div
                                className="bg-primary h-2 rounded-full transition-all duration-300"
                                style={{ width: `${(processProgress.sent / processProgress.total) * 100}%` }}
                              />
                            </div>
                          </div>
                        ) : (
                          // While in "sending" status, the messages are on the
                          // queue and the email-sender consumer drains them as
                          // fast as Resend allows. There's no useful cron-based
                          // countdown to show — the cron only kicks off the
                          // initial enqueue, which has already happened.
                          <div className="flex items-center gap-2 text-xs text-emerald-700 bg-emerald-50 px-3 h-9 rounded-md border border-emerald-200">
                            <span className="relative flex w-2 h-2">
                              <span className="absolute inset-0 rounded-full bg-emerald-400 opacity-75 animate-ping" />
                              <span className="relative inline-flex w-2 h-2 rounded-full bg-emerald-600" />
                            </span>
                            <span className="font-medium">Delivering…</span>
                            <span className="text-emerald-500">·</span>
                            <span className="tabular-nums">
                              {campaign.totalSent ?? 0}/{campaign.totalRecipients} sent
                            </span>
                          </div>
                        )}
                        <Button
                          variant="default"
                          size="sm"
                          onClick={() => handleProcessQueue(campaign.id)}
                          disabled={processingCampaignId !== null}
                          className="h-9"
                        >
                          <FiPlay className="w-4 h-4 mr-1.5" />
                          {processingCampaignId === campaign.id ? "Sending..." : "Send Now"}
                        </Button>
                      </div>
                    )}

                    {/* Actions — every Button uses size="sm" + h-9 + same px so the row stays one consistent height. Icon-only buttons get w-9 to stay square. */}
                    <div className="flex items-center gap-1.5 shrink-0">
                      {(campaign.status === "sent" || campaign.totalSent > 0) && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => openRecipients(campaign)}
                          className="h-9"
                          title="See who got this email"
                        >
                          <FiUsers className="w-4 h-4 mr-1.5" />
                          Recipients
                        </Button>
                      )}
                      {(campaign.status === "draft" || campaign.status === "scheduled") && (
                        <>
                          <Button variant="outline" size="sm" asChild className="h-9 w-9 p-0">
                            <Link to={`/admin/campaigns/edit/${campaign.id}`} title="Edit">
                              <FiEdit2 className="w-4 h-4" />
                            </Link>
                          </Button>
                          <Button
                            variant="default"
                            size="sm"
                            onClick={() => {
                              setSelectedCampaign(campaign);
                              setSendModalOpen(true);
                            }}
                            className="h-9"
                          >
                            <FiSend className="w-4 h-4 mr-1.5" />
                            Send
                          </Button>
                        </>
                      )}
                      {(campaign.status === "scheduled" || campaign.status === "sending") && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            setSelectedCampaign(campaign);
                            setCancelModalOpen(true);
                          }}
                          className="h-9 text-amber-700 hover:text-amber-800 hover:bg-amber-50 border-amber-200"
                          title="Cancel this campaign — pending sends will be skipped"
                        >
                          <FiSlash className="w-4 h-4 mr-1.5" />
                          Cancel
                        </Button>
                      )}
                      {(campaign.status === "cancelled" || campaign.status === "sent") && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => openRestartDialog(campaign)}
                          className="h-9 text-blue-700 hover:text-blue-800 hover:bg-blue-50 border-blue-200"
                          title={
                            campaign.status === "cancelled"
                              ? "Restart this cancelled campaign — choose a new send time"
                              : "Re-send this campaign to the same audience"
                          }
                        >
                          <FiRefreshCw className="w-4 h-4 mr-1.5" />
                          {campaign.status === "cancelled" ? "Restart" : "Re-send"}
                        </Button>
                      )}
                      {/* Duplicate is always available — clones content into a fresh draft. */}
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleDuplicate(campaign)}
                        disabled={duplicatingCampaignId === campaign.id}
                        className="h-9 w-9 p-0 text-slate-600 border-slate-200 hover:text-primary hover:bg-primary/5 hover:border-primary/30 transition-colors"
                        title="Duplicate as a new draft"
                      >
                        <FiCopy className={`w-4 h-4 ${duplicatingCampaignId === campaign.id ? "animate-pulse" : ""}`} />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          setSelectedCampaign(campaign);
                          setDeleteModalOpen(true);
                        }}
                        className="h-9 w-9 p-0 text-red-600 hover:text-red-700 hover:bg-red-50"
                        title={
                          campaign.status === "sending"
                            ? "Delete a sending campaign — in-flight messages will be skipped"
                            : "Delete this campaign"
                        }
                      >
                        <FiTrash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Send Confirmation Modal */}
      <Dialog open={sendModalOpen} onOpenChange={setSendModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FiSend className="w-5 h-5" />
              Send Campaign
            </DialogTitle>
            <DialogDescription>
              Are you sure you want to send this campaign?
            </DialogDescription>
          </DialogHeader>
          {selectedCampaign && (
            <div className="space-y-4">
              <div className="p-4 bg-muted rounded-lg">
                <p className="font-medium">{selectedCampaign.name}</p>
                <p className="text-sm text-muted-foreground mt-1">
                  Subject: {selectedCampaign.subject}
                </p>
                <p className="text-sm text-muted-foreground">
                  Segment: {segmentLabels[selectedCampaign.segment] || selectedCampaign.segment}
                </p>
              </div>
              <div className="flex items-start gap-2 p-3 bg-amber-50 text-amber-800 rounded-lg text-sm">
                <FiAlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
                <p>
                  This will queue the campaign for all active subscribers in the selected segment.
                  After queuing, click "Send Now" to start delivering emails.
                </p>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setSendModalOpen(false)}
              disabled={isSending}
            >
              Cancel
            </Button>
            <Button onClick={handleSend} disabled={isSending}>
              {isSending ? "Queuing..." : "Queue Campaign"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Cancel Confirmation Modal */}
      <Dialog open={cancelModalOpen} onOpenChange={setCancelModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-amber-700">
              <FiSlash className="w-5 h-5" />
              Cancel Campaign
            </DialogTitle>
            <DialogDescription>
              Stop this campaign from sending. Any emails not yet delivered will be skipped.
              The campaign record stays so you can review what was sent.
            </DialogDescription>
          </DialogHeader>
          {selectedCampaign && (
            <div className="p-4 bg-muted rounded-lg space-y-2">
              <p className="font-medium">{selectedCampaign.name}</p>
              <p className="text-sm text-muted-foreground">
                Subject: {selectedCampaign.subject}
              </p>
              <div className="flex items-center gap-2 pt-1">
                <Badge variant="outline" className="text-[11px]">
                  {selectedCampaign.status}
                </Badge>
                {selectedCampaign.totalSent > 0 && (
                  <span className="text-xs text-muted-foreground">
                    {selectedCampaign.totalSent} of {selectedCampaign.totalRecipients} already sent
                  </span>
                )}
              </div>
            </div>
          )}
          {selectedCampaign?.status === "sending" && (
            <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-3 py-2">
              <strong>Heads up:</strong> a small batch (up to ~10 emails) may already be in flight
              and could still be delivered before the cancel takes effect. To stop them too,
              cancel and then delete the campaign.
            </p>
          )}
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setCancelModalOpen(false)}
              disabled={isCancelling}
            >
              Keep Sending
            </Button>
            <Button
              variant="default"
              onClick={handleCancelConfirm}
              disabled={isCancelling}
              className="bg-amber-600 hover:bg-amber-700 text-white"
            >
              {isCancelling ? "Cancelling..." : "Cancel Campaign"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Modal */}
      <Dialog open={deleteModalOpen} onOpenChange={setDeleteModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <FiTrash2 className="w-5 h-5" />
              Delete Campaign
            </DialogTitle>
            <DialogDescription>
              Are you sure you want to delete this campaign?
            </DialogDescription>
          </DialogHeader>
          {selectedCampaign && (
            <div className="p-4 bg-muted rounded-lg">
              <p className="font-medium">{selectedCampaign.name}</p>
              <p className="text-sm text-muted-foreground mt-1">
                Subject: {selectedCampaign.subject}
              </p>
            </div>
          )}
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setDeleteModalOpen(false)}
              disabled={isDeleting}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleDelete}
              disabled={isDeleting}
            >
              {isDeleting ? "Deleting..." : "Delete"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Re-send / Restart Confirmation Modal */}
      <Dialog open={restartModalOpen} onOpenChange={setRestartModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-blue-700">
              <FiRefreshCw className="w-5 h-5" />
              {selectedCampaign?.status === "cancelled" ? "Restart Campaign" : "Re-send Campaign"}
            </DialogTitle>
            <DialogDescription>
              {selectedCampaign?.status === "cancelled"
                ? "This will re-queue every recipient and start sending again."
                : "This will send the same email to the same audience again. Recipients will receive a duplicate."}
            </DialogDescription>
          </DialogHeader>
          {selectedCampaign && (
            <div className="p-4 bg-muted rounded-lg space-y-3">
              <div>
                <p className="font-medium">{selectedCampaign.name}</p>
                <p className="text-sm text-muted-foreground">Subject: {selectedCampaign.subject}</p>
                {selectedCampaign.previewText && (
                  <p className="text-xs text-muted-foreground mt-1 italic line-clamp-1">
                    {selectedCampaign.previewText}
                  </p>
                )}
              </div>

              {/* Content snippet so the broker can confirm what they're re-sending. */}
              {selectedCampaign.content && (
                <div className="border-t border-border/50 pt-3">
                  <p className="text-[11px] uppercase tracking-wide text-muted-foreground mb-1">Email content (first 400 chars)</p>
                  <p className="text-xs text-foreground/80 whitespace-pre-wrap font-mono leading-snug max-h-32 overflow-y-auto">
                    {selectedCampaign.content.slice(0, 400)}
                    {selectedCampaign.content.length > 400 ? "…" : ""}
                  </p>
                  <button
                    type="button"
                    onClick={() => navigate(`/admin/campaigns/edit/${selectedCampaign.id}`)}
                    className="text-xs text-blue-700 hover:underline mt-1"
                  >
                    Open full editor →
                  </button>
                </div>
              )}

              <div className="flex items-center gap-2 pt-1 border-t border-border/50">
                <Badge variant="outline" className="text-[11px]">
                  {selectedCampaign.status}
                </Badge>
                {selectedCampaign.totalSent > 0 && (
                  <span className="text-xs text-muted-foreground">
                    Previously sent to {selectedCampaign.totalSent} of {selectedCampaign.totalRecipients}
                  </span>
                )}
              </div>
            </div>
          )}

          {/* New send time picker. Empty = send immediately on next cron tick. */}
          <div>
            <label className="text-sm font-medium text-slate-900 mb-1.5 block">
              When to send
            </label>
            <input
              type="datetime-local"
              value={restartScheduledFor}
              onChange={(e) => setRestartScheduledFor(e.target.value)}
              min={new Date(Date.now() - new Date().getTimezoneOffset() * 60_000).toISOString().slice(0, 16)}
              className="w-full px-3 py-2 rounded-md border border-border bg-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <p className="text-xs text-muted-foreground mt-1">
              {restartScheduledFor
                ? `Will be queued at the next cron tick after ${new Date(restartScheduledFor).toLocaleString()}.`
                : "Leave blank to send on the next cron tick (within 5 minutes)."}
            </p>
          </div>

          <p className="text-xs text-blue-700 bg-blue-50 border border-blue-200 rounded-md px-3 py-2">
            Old send records are wiped and progress counters reset.
          </p>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setRestartModalOpen(false)}
              disabled={isRestarting}
            >
              Keep As-Is
            </Button>
            <Button
              variant="default"
              onClick={handleRestartConfirm}
              disabled={isRestarting}
              className="bg-blue-600 hover:bg-blue-700 text-white"
            >
              <FiRefreshCw className={`w-4 h-4 mr-1.5 ${isRestarting ? "animate-spin" : ""}`} />
              {isRestarting
                ? (selectedCampaign?.status === "cancelled" ? "Restarting..." : "Re-sending...")
                : restartScheduledFor
                  ? "Schedule"
                  : (selectedCampaign?.status === "cancelled" ? "Restart Now" : "Re-send Now")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Recipients Drill-Down Modal */}
      <Dialog open={recipientsModalOpen} onOpenChange={setRecipientsModalOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FiUsers className="w-5 h-5 text-primary" />
              Recipients
            </DialogTitle>
            <DialogDescription>
              {selectedCampaign?.name} — {selectedCampaign?.subject}
            </DialogDescription>
          </DialogHeader>
          {recipientRows === null ? (
            <p className="text-sm text-muted-foreground text-center py-8">Loading…</p>
          ) : recipientRows.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-8">No recipients on file for this campaign.</p>
          ) : (
            <>
              <div className="flex items-center gap-3 text-xs text-muted-foreground border-b pb-2">
                <span>{recipientRows.length} total</span>
                <span className="text-emerald-700">
                  ✓ {recipientRows.filter(r => r.status === "sent" || r.status === "delivered").length} sent
                </span>
                <span className="text-red-600">
                  ✗ {recipientRows.filter(r => r.status === "failed" || r.status === "bounced").length} failed
                </span>
                <span className="text-amber-600">
                  • {recipientRows.filter(r => r.status === "pending").length} pending
                </span>
                <Input
                  placeholder="Filter…"
                  value={recipientSearch}
                  onChange={(e) => setRecipientSearch(e.target.value)}
                  className="h-8 ml-auto max-w-[200px]"
                />
              </div>
              <div className="max-h-[420px] overflow-y-auto -mx-6 px-6">
                <table className="w-full text-sm">
                  <thead className="text-[11px] uppercase tracking-wide text-muted-foreground sticky top-0 bg-background z-10">
                    <tr className="border-b">
                      <th className="text-left py-2 font-medium">Email</th>
                      <th className="text-left py-2 font-medium">Status</th>
                      <th className="text-left py-2 font-medium">Sent</th>
                      <th className="text-left py-2 font-medium">Opened</th>
                    </tr>
                  </thead>
                  <tbody>
                    {recipientRows
                      .filter(r =>
                        !recipientSearch ||
                        r.email.toLowerCase().includes(recipientSearch.toLowerCase()),
                      )
                      .map(row => {
                        const statusColor =
                          row.status === "sent" || row.status === "delivered" ? "text-emerald-700 bg-emerald-50 border-emerald-200" :
                          row.status === "failed" || row.status === "bounced" ? "text-red-700 bg-red-50 border-red-200" :
                          "text-amber-700 bg-amber-50 border-amber-200";
                        return (
                          <tr key={row.id} className="border-b border-border/40 hover:bg-muted/40">
                            <td className="py-2 font-mono text-xs truncate max-w-[260px]">{row.email}</td>
                            <td className="py-2">
                              <span className={`text-[11px] px-2 py-0.5 rounded-full font-medium border ${statusColor}`}>
                                {row.status}
                              </span>
                            </td>
                            <td className="py-2 text-xs text-muted-foreground tabular-nums">
                              {row.sentAt ? new Date(row.sentAt).toLocaleString() : "—"}
                            </td>
                            <td className="py-2 text-xs text-muted-foreground tabular-nums">
                              {row.openedAt ? new Date(row.openedAt).toLocaleString() : "—"}
                            </td>
                          </tr>
                        );
                      })}
                  </tbody>
                </table>
              </div>
            </>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setRecipientsModalOpen(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
